import React, { useCallback, useEffect, useState } from 'react';
import { DollarSign, Users, Building2, TrendingDown, Edit2, X, AlertCircle, CheckCircle2, FileText, RefreshCw, ChevronDown, Search, Wallet, ArrowUpRight, ArrowDownRight, Clock, Plus, Zap, CheckSquare, Square, Loader2, Calendar, Settings2, PlayCircle, ToggleLeft, ToggleRight } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { useAuth } from '../contexts/AuthContext';
import { supabaseClient, isSupabaseConfigured } from '../lib/supabase';
import { useDocumentTitle } from '../utils/useDocumentTitle';
import { MONTH_NAMES, EmployeeBalance, BalanceLog, formatMonth } from '../contexts/BalanceContext';
import { useToast } from '../components/ui/Toast';
const ACTION_LABELS: Record<string, {
  label: string;
  color: string;
}> = {
  balance_assigned: {
    label: 'Asignado',
    color: 'bg-emerald-100 text-emerald-700 border-emerald-200'
  },
  balance_modified: {
    label: 'Modificado',
    color: 'bg-blue-100 text-blue-700 border-blue-200'
  },
  balance_consumed: {
    label: 'Consumido',
    color: 'bg-orange-100 text-orange-700 border-orange-200'
  },
  subsidy_enabled: {
    label: 'Subsidio Activado',
    color: 'bg-purple-100 text-purple-700 border-purple-200'
  },
  subsidy_disabled: {
    label: 'Subsidio Desactivado',
    color: 'bg-red-100 text-red-700 border-red-200'
  }
};
export function SubsidyManagementPage() {
  useDocumentTitle('Gestión de Subsidios');
  const {
    user
  } = useAuth();
  const {
    showToast
  } = useToast();
  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();
  const [activeTab, setActiveTab] = useState<'balances' | 'logs'>('balances');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  // Data
  const [companies, setCompanies] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [balances, setBalances] = useState<EmployeeBalance[]>([]);
  const [logs, setLogs] = useState<BalanceLog[]>([]);
  // Filters
  const [filterCompany, setFilterCompany] = useState('all');
  const [filterMonth, setFilterMonth] = useState(currentMonth);
  const [filterYear, setFilterYear] = useState(currentYear);
  const [logFilterCompany, setLogFilterCompany] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  // Modify modal
  const [modifyModal, setModifyModal] = useState(false);
  const [modifyingBalance, setModifyingBalance] = useState<EmployeeBalance | null>(null);
  const [modifyForm, setModifyForm] = useState({
    adjustment: '',
    notes: ''
  });
  const [modifyLoading, setModifyLoading] = useState(false);
  const [modifyError, setModifyError] = useState('');
  // Assign modal (NEW)
  const [assignModal, setAssignModal] = useState(false);
  const [assignForm, setAssignForm] = useState({
    employeeId: '',
    companyId: '',
    month: currentMonth,
    year: currentYear,
    amount: '',
    notes: ''
  });
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignError, setAssignError] = useState('');
  // Bulk assign modal state
  const [bulkAssignModal, setBulkAssignModal] = useState(false);
  const [bulkCompanyId, setBulkCompanyId] = useState('');
  const [bulkMonth, setBulkMonth] = useState(currentMonth);
  const [bulkYear, setBulkYear] = useState(currentYear);
  const [bulkAmount, setBulkAmount] = useState('');
  const [bulkNotes, setBulkNotes] = useState('');
  const [bulkSelectedIds, setBulkSelectedIds] = useState<string[]>([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkError, setBulkError] = useState('');
  const [bulkProgress, setBulkProgress] = useState<{
    done: number;
    total: number;
    skipped: number;
    errors: number;
  } | null>(null);
  const [bulkDone, setBulkDone] = useState(false);
  const [autoAssignModal, setAutoAssignModal] = useState(false);
  const [autoRules, setAutoRules] = useState<{
    companyId: string;
    amount: string;
    enabled: boolean;
  }[]>([]);
  const [autoRunLoading, setAutoRunLoading] = useState(false);
  const [autoRunResult, setAutoRunResult] = useState<{
    done: number;
    skipped: number;
    errors: number;
  } | null>(null);
  const subsidyCompanies = companies.filter(c => c.subsidyEnabled);
  // Employees from subsidy companies (for assign modal)
  const subsidyEmployees = employees.filter(e => subsidyCompanies.some(c => String(c.id) === String(e.company_id || e.companyId)));
  // Employees for the selected bulk company
  const bulkCompanyEmployees = employees.filter(e => String(e.company_id || e.companyId) === bulkCompanyId);
  const loadData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);else setRefreshing(true);
    try {
      const [comps, emps] = await Promise.all([supabaseClient.getCompanies(), supabaseClient.getEmployees()]);
      setCompanies(comps);
      setEmployees(emps);
    } catch (e) {
      console.error('Error loading base data:', e);
    } finally {
      if (!silent) setLoading(false);else setRefreshing(false);
    }
  }, []);
  const loadBalances = useCallback(async () => {
    if (!isSupabaseConfigured()) return;
    try {
      const data = await supabaseClient.getAllBalances(filterMonth, filterYear);
      setBalances(data);
    } catch (e) {
      console.error('Error loading balances:', e);
    }
  }, [filterMonth, filterYear]);
  const loadLogs = useCallback(async () => {
    if (!isSupabaseConfigured()) return;
    try {
      const data = await supabaseClient.getBalanceLogs({
        companyId: logFilterCompany !== 'all' ? logFilterCompany : undefined,
        limit: 200
      });
      setLogs(data);
    } catch (e) {
      console.error('Error loading logs:', e);
    }
  }, [logFilterCompany]);
  useEffect(() => {
    loadData();
  }, [loadData]);
  useEffect(() => {
    loadBalances();
  }, [loadBalances]);
  useEffect(() => {
    loadLogs();
  }, [loadLogs]);
  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([loadData(true), loadBalances(), loadLogs()]);
    setRefreshing(false);
  };
  // Helpers
  const getEmployee = (id: string) => employees.find(e => e.id === id);
  const getCompany = (id: string) => companies.find(c => String(c.id) === String(id));
  // Filtered balances
  const filteredBalances = balances.filter(b => {
    const emp = getEmployee(b.employeeId);
    const matchesCompany = filterCompany === 'all' || String(b.companyId) === filterCompany;
    const matchesSearch = !searchTerm || emp?.name?.toLowerCase().includes(searchTerm.toLowerCase()) || emp?.email?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCompany && matchesSearch;
  });
  // Stats
  const totalAssigned = filteredBalances.reduce((s, b) => s + b.assignedAmount, 0);
  const totalAvailable = filteredBalances.reduce((s, b) => s + b.currentBalance, 0);
  const totalConsumed = totalAssigned - totalAvailable;
  const handleOpenModify = (balance: EmployeeBalance) => {
    setModifyingBalance(balance);
    setModifyForm({
      adjustment: '',
      notes: ''
    });
    setModifyError('');
    setModifyModal(true);
  };
  const handleOpenAssign = () => {
    setAssignForm({
      employeeId: '',
      companyId: subsidyCompanies[0]?.id ? String(subsidyCompanies[0].id) : '',
      month: filterMonth,
      year: filterYear,
      amount: '',
      notes: ''
    });
    setAssignError('');
    setAssignModal(true);
  };
  const handleBulkCompanyChange = (companyId: string) => {
    setBulkCompanyId(companyId);
    const emps = employees.filter(e => String(e.company_id || e.companyId) === companyId);
    setBulkSelectedIds(emps.map(e => e.id));
  };
  const toggleBulkEmployee = (id: string) => {
    setBulkSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleSelectAll = () => {
    if (bulkSelectedIds.length === bulkCompanyEmployees.length) {
      setBulkSelectedIds([]);
    } else {
      setBulkSelectedIds(bulkCompanyEmployees.map(e => e.id));
    }
  };
  const handleBulkAssign = async () => {
    if (bulkSelectedIds.length === 0) {
      setBulkError('Selecciona al menos un empleado.');
      return;
    }
    const amount = parseFloat(bulkAmount);
    if (isNaN(amount) || amount <= 0) {
      setBulkError('Ingresa un monto válido mayor a 0.');
      return;
    }
    setBulkLoading(true);
    setBulkError('');
    setBulkProgress({
      done: 0,
      total: bulkSelectedIds.length,
      skipped: 0,
      errors: 0
    });
    let done = 0;
    let skipped = 0;
    let errors = 0;
    for (const empId of bulkSelectedIds) {
      const emp = getEmployee(empId);
      const companyId = bulkCompanyId || String(emp?.company_id || emp?.companyId || '');
      try {
        await supabaseClient.assignBalance({
          employeeId: empId,
          companyId,
          month: bulkMonth,
          year: bulkYear,
          amount,
          assignedBy: user?.name || 'Admin',
          notes: bulkNotes || `Asignación masiva - ${formatMonth(bulkMonth, bulkYear)}`
        });
        await supabaseClient.createBalanceLog({
          employeeId: empId,
          employeeName: emp?.name,
          companyId,
          companyName: getCompany(companyId)?.name,
          actionType: 'balance_assigned',
          amount,
          month: bulkMonth,
          year: bulkYear,
          performedBy: user?.name || 'Admin',
          performedByRole: 'admin',
          notes: bulkNotes || `Asignación masiva para ${formatMonth(bulkMonth, bulkYear)}`
        });
        done++;
      } catch (e: any) {
        // If already exists, count as skipped
        if (e.message?.includes('Ya existe')) {
          skipped++;
        } else {
          errors++;
        }
      }
      setBulkProgress({
        done: done + skipped + errors,
        total: bulkSelectedIds.length,
        skipped,
        errors
      });
    }
    await Promise.all([loadBalances(), loadLogs()]);
    setBulkLoading(false);
    setBulkDone(true);
    setBulkProgress({
      done,
      total: bulkSelectedIds.length,
      skipped,
      errors
    });
    showToast(errors > 0 ? 'warning' : 'success', 'Asignación masiva completada', `${done} asignados · ${skipped} ya existían · ${errors} errores`);
  };
  const handleOpenBulkAssign = () => {
    const firstCompany = subsidyCompanies[0];
    const firstId = firstCompany ? String(firstCompany.id) : '';
    setBulkCompanyId(firstId);
    setBulkMonth(filterMonth);
    setBulkYear(filterYear);
    setBulkAmount('');
    setBulkNotes('');
    setBulkSelectedIds(employees.filter(e => String(e.company_id || e.companyId) === firstId).map(e => e.id));
    setBulkError('');
    setBulkProgress(null);
    setBulkDone(false);
    setBulkAssignModal(true);
  };
  const handleAssignBalance = async () => {
    if (!assignForm.employeeId || !assignForm.amount) {
      setAssignError('Selecciona un empleado e ingresa un monto.');
      return;
    }
    const amount = parseFloat(assignForm.amount);
    if (isNaN(amount) || amount <= 0) {
      setAssignError('Ingresa un monto válido mayor a 0.');
      return;
    }
    setAssignLoading(true);
    setAssignError('');
    try {
      const emp = getEmployee(assignForm.employeeId);
      const companyId = assignForm.companyId || String(emp?.company_id || emp?.companyId || '');
      await supabaseClient.assignBalance({
        employeeId: assignForm.employeeId,
        companyId,
        month: assignForm.month,
        year: assignForm.year,
        amount,
        assignedBy: user?.name || 'Admin',
        notes: assignForm.notes || undefined
      });
      await supabaseClient.createBalanceLog({
        employeeId: assignForm.employeeId,
        employeeName: emp?.name,
        companyId,
        companyName: getCompany(companyId)?.name,
        actionType: 'balance_assigned',
        amount,
        month: assignForm.month,
        year: assignForm.year,
        performedBy: user?.name || 'Admin',
        performedByRole: 'admin',
        notes: assignForm.notes || `Balance asignado por admin para ${formatMonth(assignForm.month, assignForm.year)}`
      });
      showToast('success', 'Balance asignado', `RD$ ${amount.toFixed(2)} asignado a ${emp?.name}.`);
      await Promise.all([loadBalances(), loadLogs()]);
      setAssignModal(false);
    } catch (e: any) {
      setAssignError(e.message || 'Error al asignar balance.');
    } finally {
      setAssignLoading(false);
    }
  };
  const handleModifyBalance = async () => {
    if (!modifyingBalance) return;
    const adjustment = parseFloat(modifyForm.adjustment);
    if (isNaN(adjustment) || adjustment === 0) {
      setModifyError('Ingresa un monto de ajuste válido (positivo o negativo).');
      return;
    }
    const newAssigned = modifyingBalance.assignedAmount + adjustment;
    const newCurrent = modifyingBalance.currentBalance + adjustment;
    if (newAssigned < 0) {
      setModifyError('El ajuste resultaría en un monto asignado negativo.');
      return;
    }
    // Allow negative current balance? Probably not, but let's just warn if it goes below 0
    // Actually, let's allow it but maybe warn. For now, strict check:
    if (newCurrent < 0) {
      // It's possible to have negative balance if they consumed more than what's being removed,
      // but typically we shouldn't allow removing more than available if we want to be strict.
      // However, the user wants to "add/subtract". Let's assume standard logic.
    }
    setModifyLoading(true);
    setModifyError('');
    try {
      await supabaseClient.modifyBalance({
        balanceId: modifyingBalance.id,
        newAmount: newAssigned,
        currentBalance: newCurrent,
        modifiedBy: user?.name || 'Admin',
        notes: modifyForm.notes || undefined
      });
      const emp = getEmployee(modifyingBalance.employeeId);
      await supabaseClient.createBalanceLog({
        employeeId: modifyingBalance.employeeId,
        employeeName: emp?.name,
        companyId: String(modifyingBalance.companyId),
        companyName: getCompany(String(modifyingBalance.companyId))?.name,
        actionType: 'balance_modified',
        amount: Math.abs(adjustment),
        previousAmount: modifyingBalance.assignedAmount,
        month: modifyingBalance.month,
        year: modifyingBalance.year,
        performedBy: user?.name || 'Admin',
        performedByRole: 'admin',
        notes: modifyForm.notes || `Ajuste por admin: ${adjustment > 0 ? '+' : ''}${adjustment} (Nuevo asignado: ${newAssigned})`
      });
      showToast('success', 'Balance actualizado', `Balance de ${emp?.name} ajustado en ${adjustment > 0 ? '+' : ''}${adjustment}.`);
      await Promise.all([loadBalances(), loadLogs()]);
      setModifyModal(false);
    } catch (e: any) {
      setModifyError(e.message || 'Error al modificar balance.');
    } finally {
      setModifyLoading(false);
    }
  };
  // Load auto-assign rules from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('criket_auto_subsidy_rules');
    if (saved) {
      try {
        setAutoRules(JSON.parse(saved));
      } catch {}
    }
  }, []);
  // Sync rules when companies load
  useEffect(() => {
    if (subsidyCompanies.length === 0) return;
    setAutoRules(prev => {
      const updated = subsidyCompanies.map(c => {
        const existing = prev.find(r => r.companyId === String(c.id));
        return existing || {
          companyId: String(c.id),
          amount: '',
          enabled: false
        };
      });
      return updated;
    });
  }, [subsidyCompanies.length]);
  const saveAutoRules = (rules: typeof autoRules) => {
    setAutoRules(rules);
    localStorage.setItem('criket_auto_subsidy_rules', JSON.stringify(rules));
  };
  const handleRunAutoAssign = async () => {
    const enabledRules = autoRules.filter(r => r.enabled && r.amount && parseFloat(r.amount) > 0);
    if (enabledRules.length === 0) {
      showToast('warning', 'Sin reglas activas', 'Activa al menos una empresa con un monto válido.');
      return;
    }
    setAutoRunLoading(true);
    setAutoRunResult(null);
    let done = 0,
      skipped = 0,
      errors = 0;
    for (const rule of enabledRules) {
      const companyEmployees = employees.filter(e => String(e.company_id || e.companyId) === rule.companyId);
      const amount = parseFloat(rule.amount);
      for (const emp of companyEmployees) {
        try {
          await supabaseClient.assignBalance({
            employeeId: emp.id,
            companyId: rule.companyId,
            month: currentMonth,
            year: currentYear,
            amount,
            assignedBy: user?.name || 'Admin',
            notes: `Auto-asignación ${MONTH_NAMES[currentMonth - 1]} ${currentYear}`
          });
          await supabaseClient.createBalanceLog({
            employeeId: emp.id,
            employeeName: emp.name,
            companyId: rule.companyId,
            companyName: getCompany(rule.companyId)?.name,
            actionType: 'balance_assigned',
            amount,
            month: currentMonth,
            year: currentYear,
            performedBy: user?.name || 'Admin',
            performedByRole: 'admin',
            notes: `Auto-asignación ${MONTH_NAMES[currentMonth - 1]} ${currentYear}`
          });
          done++;
        } catch (e: any) {
          if (e.message?.includes('Ya existe')) skipped++;else errors++;
        }
      }
    }
    await Promise.all([loadBalances(), loadLogs()]);
    setAutoRunLoading(false);
    setAutoRunResult({
      done,
      skipped,
      errors
    });
    showToast(errors > 0 ? 'warning' : 'success', 'Auto-asignación completada', `${done} asignados · ${skipped} ya existían · ${errors} errores`);
  };
  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]" data-id="element-5621">
        <div className="text-center" data-id="element-5622">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black dark:border-white mx-auto mb-4" data-id="element-5623" />
          <p className="text-gray-500 dark:text-gray-400" data-id="element-5624">
            Cargando subsidios...
          </p>
        </div>
      </div>;
  }
  return <div className="p-6 lg:p-10 max-w-7xl mx-auto space-y-8" data-id="element-5625">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-5626">
        <div data-id="element-5627">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white tracking-tight flex items-center gap-3" data-id="element-5628">
            <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl" data-id="element-5629">
              <DollarSign className="w-7 h-7 text-emerald-600 dark:text-emerald-400" data-id="element-5630" />
            </div>
            Gestión de Subsidios
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-2 text-lg" data-id="element-5631">
            Administra balances mensuales y audita el uso de subsidios
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap" data-id="element-5632">
          <button onClick={handleRefresh} disabled={refreshing} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50" data-id="element-5633">
            <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} data-id="element-5634" />
            Actualizar
          </button>
          {subsidyCompanies.length > 0 && <>
              <Button leftIcon={<Calendar size={16} data-id="element-5636" />} onClick={() => {
            setAutoRunResult(null);
            setAutoAssignModal(true);
          }} size="sm" variant="outline" data-id="element-5635">
                Auto-asignación
              </Button>
              <Button leftIcon={<Zap size={16} data-id="element-5638" />} onClick={handleOpenBulkAssign} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white border-none" data-id="element-5637">
                Asignación Masiva
              </Button>
              <Button leftIcon={<Plus size={16} data-id="element-5640" />} onClick={handleOpenAssign} size="sm" variant="outline" data-id="element-5639">
                Asignar uno
              </Button>
            </>}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-5641">
        <Card className="p-5 border-l-4 border-l-emerald-500" data-id="element-5642">
          <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1" data-id="element-5643">
            Total Asignado
          </p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-5644">
            RD${' '}
            {totalAssigned.toLocaleString('es-DO', {
            minimumFractionDigits: 2
          })}
          </p>
          <p className="text-xs text-gray-400 mt-1" data-id="element-5645">
            {filteredBalances.length} empleados
          </p>
        </Card>
        <Card className="p-5 border-l-4 border-l-blue-500" data-id="element-5646">
          <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1" data-id="element-5647">
            Disponible
          </p>
          <p className="text-2xl font-bold text-blue-700 dark:text-blue-400" data-id="element-5648">
            RD${' '}
            {totalAvailable.toLocaleString('es-DO', {
            minimumFractionDigits: 2
          })}
          </p>
          <p className="text-xs text-gray-400 mt-1" data-id="element-5649">
            {totalAssigned > 0 ? (totalAvailable / totalAssigned * 100).toFixed(0) : 0}
            % restante
          </p>
        </Card>
        <Card className="p-5 border-l-4 border-l-orange-500" data-id="element-5650">
          <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1" data-id="element-5651">
            Consumido
          </p>
          <p className="text-2xl font-bold text-orange-700 dark:text-orange-400" data-id="element-5652">
            RD${' '}
            {totalConsumed.toLocaleString('es-DO', {
            minimumFractionDigits: 2
          })}
          </p>
          <p className="text-xs text-gray-400 mt-1" data-id="element-5653">
            {totalAssigned > 0 ? (totalConsumed / totalAssigned * 100).toFixed(0) : 0}
            % usado
          </p>
        </Card>
        <Card className="p-5 border-l-4 border-l-purple-500" data-id="element-5654">
          <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1" data-id="element-5655">
            Empresas Activas
          </p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-5656">
            {subsidyCompanies.length}
          </p>
          <p className="text-xs text-gray-400 mt-1" data-id="element-5657">con subsidio habilitado</p>
        </Card>
      </div>

      {/* No subsidy companies warning */}
      {subsidyCompanies.length === 0 && <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl" data-id="element-5658">
          <AlertCircle size={18} className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" data-id="element-5659" />
          <div data-id="element-5660">
            <p className="text-sm font-bold text-amber-800 dark:text-amber-200" data-id="element-5661">
              Ninguna empresa tiene subsidio activo
            </p>
            <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5" data-id="element-5662">
              Ve a <strong data-id="element-5663">Empresas → Editar</strong> y activa el toggle{' '}
              <strong data-id="element-5664">"Subsidio Activo"</strong> para poder asignar balances.
            </p>
          </div>
        </div>}

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-800" data-id="element-5665">
        <div className="flex gap-1" data-id="element-5666">
          {[{
          id: 'balances',
          label: 'Gestión de Balances',
          icon: Wallet
        }, {
          id: 'logs',
          label: 'Logs de Auditoría',
          icon: FileText
        }].map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id as any)} className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold border-b-2 transition-colors ${activeTab === tab.id ? 'border-gray-900 dark:border-white text-gray-900 dark:text-white' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-5667">
              <tab.icon size={16} data-id="element-5668" />
              {tab.label}
            </button>)}
        </div>
      </div>

      {/* BALANCES TAB */}
      {activeTab === 'balances' && <div className="space-y-6" data-id="element-5669">
          {/* Filters — improved design */}
          <div className="bg-white dark:bg-gray-900 p-5 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm" data-id="element-5670">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4" data-id="element-5671">
              Filtros
            </p>
            <div className="flex flex-col sm:flex-row gap-3" data-id="element-5672">
              {/* Search */}
              <div className="flex-1" data-id="element-5673">
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5" data-id="element-5674">
                  Empleado
                </label>
                <div className="relative" data-id="element-5675">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} data-id="element-5676" />
                  <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Buscar por nombre..." className="w-full pl-9 pr-4 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white" data-id="element-5677" />
                </div>
              </div>
              {/* Company */}
              <div className="sm:w-52" data-id="element-5678">
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5" data-id="element-5679">
                  Empresa
                </label>
                <select value={filterCompany} onChange={e => setFilterCompany(e.target.value)} className="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white" data-id="element-5680">
                  <option value="all" data-id="element-5681">Todas las empresas</option>
                  {subsidyCompanies.map(c => <option key={c.id} value={String(c.id)} data-id="element-5682">
                      {c.name}
                    </option>)}
                </select>
              </div>
              {/* Month */}
              <div className="sm:w-36" data-id="element-5683">
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5" data-id="element-5684">
                  Mes
                </label>
                <select value={filterMonth} onChange={e => setFilterMonth(parseInt(e.target.value))} className="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white" data-id="element-5685">
                  {MONTH_NAMES.map((m, i) => <option key={i} value={i + 1} data-id="element-5686">
                      {m}
                    </option>)}
                </select>
              </div>
              {/* Year */}
              <div className="sm:w-24" data-id="element-5687">
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5" data-id="element-5688">
                  Año
                </label>
                <input type="number" value={filterYear} onChange={e => setFilterYear(parseInt(e.target.value))} min={2024} max={2099} className="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white" data-id="element-5689" />
              </div>
            </div>
            {/* Active filters summary */}
            {(filterCompany !== 'all' || searchTerm) && <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100 dark:border-gray-800" data-id="element-5690">
                <span className="text-xs text-gray-400" data-id="element-5691">Filtros activos:</span>
                {filterCompany !== 'all' && <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-xs font-semibold rounded-full" data-id="element-5692">
                    {subsidyCompanies.find(c => String(c.id) === filterCompany)?.name}
                    <button onClick={() => setFilterCompany('all')} className="text-gray-400 hover:text-gray-600" data-id="element-5693">
                      <X size={12} data-id="element-5694" />
                    </button>
                  </span>}
                {searchTerm && <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-xs font-semibold rounded-full" data-id="element-5695">
                    "{searchTerm}"
                    <button onClick={() => setSearchTerm('')} className="text-gray-400 hover:text-gray-600" data-id="element-5696">
                      <X size={12} data-id="element-5697" />
                    </button>
                  </span>}
              </div>}
          </div>

          {filteredBalances.length === 0 ? <div className="text-center py-20 bg-white dark:bg-gray-900 rounded-2xl border border-dashed border-gray-200 dark:border-gray-800" data-id="element-5698">
              <div className="w-16 h-16 bg-gray-50 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-5699">
                <DollarSign size={32} className="text-gray-400" data-id="element-5700" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-5701">
                Sin balances registrados
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mt-2" data-id="element-5702">
                No hay balances para {formatMonth(filterMonth, filterYear)} con
                los filtros seleccionados.
              </p>
              {subsidyCompanies.length > 0 && <button onClick={handleOpenAssign} className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-black text-sm font-bold rounded-xl hover:opacity-90 transition-opacity" data-id="element-5703">
                  <Plus size={16} data-id="element-5704" /> Asignar primer balance
                </button>}
            </div> : <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 overflow-hidden shadow-sm" data-id="element-5705">
              <div className="overflow-x-auto" data-id="element-5706">
                <table className="w-full text-left border-collapse min-w-[800px]" data-id="element-5707">
                  <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-800" data-id="element-5708">
                    <tr data-id="element-5709">
                      <th className="px-6 py-4 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-5710">
                        Empleado
                      </th>
                      <th className="px-6 py-4 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-5711">
                        Empresa
                      </th>
                      <th className="px-6 py-4 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-5712">
                        Asignado
                      </th>
                      <th className="px-6 py-4 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-5713">
                        Disponible
                      </th>
                      <th className="px-6 py-4 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-5714">
                        Uso
                      </th>
                      <th className="px-6 py-4 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-5715">
                        Asignado por
                      </th>
                      <th className="px-6 py-4 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right" data-id="element-5716">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-800" data-id="element-5717">
                    {filteredBalances.map(balance => {
                const emp = getEmployee(balance.employeeId);
                const comp = getCompany(String(balance.companyId));
                const usagePct = balance.assignedAmount > 0 ? (balance.assignedAmount - balance.currentBalance) / balance.assignedAmount * 100 : 0;
                const isLow = balance.currentBalance < balance.assignedAmount * 0.2;
                const isDepleted = balance.currentBalance <= 0;
                return <tr key={balance.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors" data-id="element-5718">
                          <td className="px-6 py-4" data-id="element-5719">
                            <div className="flex items-center gap-3" data-id="element-5720">
                              <div className="h-9 w-9 rounded-full bg-gradient-to-br from-emerald-100 to-emerald-200 dark:from-emerald-900/40 dark:to-emerald-800/40 flex items-center justify-center text-emerald-700 dark:text-emerald-300 font-bold text-sm shrink-0" data-id="element-5721">
                                {emp?.name?.charAt(0) || '?'}
                              </div>
                              <div data-id="element-5722">
                                <p className="text-sm font-bold text-gray-900 dark:text-white" data-id="element-5723">
                                  {emp?.name || balance.employeeId}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-5724">
                                  {emp?.email || ''}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4" data-id="element-5725">
                            <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-5726">
                              {comp?.name || balance.companyId}
                            </span>
                          </td>
                          <td className="px-6 py-4" data-id="element-5727">
                            <span className="text-sm font-bold text-gray-900 dark:text-white font-mono" data-id="element-5728">
                              RD$ {balance.assignedAmount.toFixed(2)}
                            </span>
                          </td>
                          <td className="px-6 py-4" data-id="element-5729">
                            <span className={`text-sm font-bold font-mono ${isDepleted ? 'text-red-600 dark:text-red-400' : isLow ? 'text-orange-600 dark:text-orange-400' : 'text-emerald-600 dark:text-emerald-400'}`} data-id="element-5730">
                              RD$ {balance.currentBalance.toFixed(2)}
                            </span>
                          </td>
                          <td className="px-6 py-4" data-id="element-5731">
                            <div className="space-y-1.5" data-id="element-5732">
                              <div className="flex items-center justify-between" data-id="element-5733">
                                <span className={`text-xs font-bold ${isDepleted ? 'text-red-600' : isLow ? 'text-orange-600' : 'text-gray-600 dark:text-gray-400'}`} data-id="element-5734">
                                  {usagePct.toFixed(0)}%
                                </span>
                              </div>
                              <div className="w-28 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-5735">
                                <div className={`h-full rounded-full transition-all ${isDepleted ? 'bg-red-500' : isLow ? 'bg-orange-500' : 'bg-emerald-500'}`} style={{
                          width: `${Math.min(100, usagePct)}%`
                        }} data-id="element-5736" />
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4" data-id="element-5737">
                            <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-5738">
                              {balance.assignedBy}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right" data-id="element-5739">
                            <button onClick={() => handleOpenModify(balance)} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors" title="Modificar balance" data-id="element-5740">
                              <Edit2 size={16} data-id="element-5741" />
                            </button>
                          </td>
                        </tr>;
              })}
                  </tbody>
                </table>
              </div>
            </div>}
        </div>}

      {/* LOGS TAB */}
      {activeTab === 'logs' && <div className="space-y-6" data-id="element-5742">
          {/* Filters */}
          <div className="bg-white dark:bg-gray-900 p-4 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm flex flex-col sm:flex-row gap-3" data-id="element-5743">
            <select value={logFilterCompany} onChange={e => setLogFilterCompany(e.target.value)} className="px-3 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white" data-id="element-5744">
              <option value="all" data-id="element-5745">Todas las empresas</option>
              {subsidyCompanies.map(c => <option key={c.id} value={String(c.id)} data-id="element-5746">
                  {c.name}
                </option>)}
            </select>
            <p className="text-sm text-gray-500 dark:text-gray-400 self-center ml-auto" data-id="element-5747">
              {logs.length} registros
            </p>
          </div>

          {logs.length === 0 ? <div className="text-center py-20 bg-white dark:bg-gray-900 rounded-2xl border border-dashed border-gray-200 dark:border-gray-800" data-id="element-5748">
              <div className="w-16 h-16 bg-gray-50 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-5749">
                <FileText size={32} className="text-gray-400" data-id="element-5750" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-5751">
                Sin registros de auditoría
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mt-2" data-id="element-5752">
                Los eventos de subsidios aparecerán aquí.
              </p>
            </div> : <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 overflow-hidden shadow-sm" data-id="element-5753">
              <div className="overflow-x-auto" data-id="element-5754">
                <table className="w-full text-left border-collapse min-w-[700px]" data-id="element-5755">
                  <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-800" data-id="element-5756">
                    <tr data-id="element-5757">
                      <th className="px-6 py-4 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-5758">
                        Fecha
                      </th>
                      <th className="px-6 py-4 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-5759">
                        Acción
                      </th>
                      <th className="px-6 py-4 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-5760">
                        Empleado
                      </th>
                      <th className="px-6 py-4 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-5761">
                        Empresa
                      </th>
                      <th className="px-6 py-4 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-5762">
                        Monto
                      </th>
                      <th className="px-6 py-4 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-5763">
                        Realizado por
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-800" data-id="element-5764">
                    {logs.map(log => {
                const actionInfo = ACTION_LABELS[log.actionType] || {
                  label: log.actionType,
                  color: 'bg-gray-100 text-gray-700 border-gray-200'
                };
                const logDate = new Date(log.createdAt);
                return <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors" data-id="element-5765">
                          <td className="px-6 py-4" data-id="element-5766">
                            <div data-id="element-5767">
                              <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-5768">
                                {logDate.toLocaleDateString('es-DO', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        })}
                              </p>
                              <p className="text-xs text-gray-400" data-id="element-5769">
                                {logDate.toLocaleTimeString('es-DO', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                              </p>
                            </div>
                          </td>
                          <td className="px-6 py-4" data-id="element-5770">
                            <span className={`inline-flex items-center px-2.5 py-1 text-xs font-bold rounded-full border ${actionInfo.color}`} data-id="element-5771">
                              {actionInfo.label}
                            </span>
                          </td>
                          <td className="px-6 py-4" data-id="element-5772">
                            <p className="text-sm text-gray-900 dark:text-white" data-id="element-5773">
                              {log.employeeName || '—'}
                            </p>
                            {log.month && log.year && <p className="text-xs text-gray-400" data-id="element-5774">
                                {formatMonth(log.month, log.year)}
                              </p>}
                          </td>
                          <td className="px-6 py-4" data-id="element-5775">
                            <p className="text-sm text-gray-700 dark:text-gray-300" data-id="element-5776">
                              {log.companyName || '—'}
                            </p>
                          </td>
                          <td className="px-6 py-4" data-id="element-5777">
                            {log.amount !== null && log.amount !== undefined ? <div data-id="element-5778">
                                <p className="text-sm font-bold font-mono text-gray-900 dark:text-white" data-id="element-5779">
                                  RD$ {log.amount.toFixed(2)}
                                </p>
                                {log.previousAmount !== null && log.previousAmount !== undefined && <p className="text-xs text-gray-400" data-id="element-5780">
                                      Anterior: RD${' '}
                                      {log.previousAmount.toFixed(2)}
                                    </p>}
                              </div> : <span className="text-gray-400" data-id="element-5781">—</span>}
                          </td>
                          <td className="px-6 py-4" data-id="element-5782">
                            <p className="text-sm text-gray-700 dark:text-gray-300" data-id="element-5783">
                              {log.performedBy}
                            </p>
                            <p className="text-xs text-gray-400 capitalize" data-id="element-5784">
                              {log.performedByRole || ''}
                            </p>
                          </td>
                        </tr>;
              })}
                  </tbody>
                </table>
              </div>
            </div>}
        </div>}

      {/* AUTO-ASSIGN MODAL */}
      {autoAssignModal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-id="element-5785">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-lg border border-gray-200 dark:border-gray-800 flex flex-col max-h-[90vh]" data-id="element-5786">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800 shrink-0" data-id="element-5787">
              <div className="flex items-center gap-3" data-id="element-5788">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-xl" data-id="element-5789">
                  <Calendar size={20} className="text-blue-600 dark:text-blue-400" data-id="element-5790" />
                </div>
                <div data-id="element-5791">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-5792">
                    Auto-asignación Mensual
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-5793">
                    {MONTH_NAMES[currentMonth - 1]} {currentYear} — asigna a
                    todos los empleados activos
                  </p>
                </div>
              </div>
              <button onClick={() => setAutoAssignModal(false)} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors" data-id="element-5794">
                <X size={18} data-id="element-5795" />
              </button>
            </div>

            <div className="overflow-y-auto flex-1 p-6 space-y-4" data-id="element-5796">
              {autoRunResult ? <div className="text-center py-4 space-y-4" data-id="element-5797">
                  <div className="w-14 h-14 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mx-auto" data-id="element-5798">
                    <CheckCircle2 size={28} className="text-emerald-600 dark:text-emerald-400" data-id="element-5799" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-5800">
                    ¡Auto-asignación ejecutada!
                  </h3>
                  <div className="grid grid-cols-3 gap-3" data-id="element-5801">
                    <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-3 text-center border border-emerald-200 dark:border-emerald-800" data-id="element-5802">
                      <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-400" data-id="element-5803">
                        {autoRunResult.done}
                      </p>
                      <p className="text-xs text-emerald-600 font-medium mt-0.5" data-id="element-5804">
                        Asignados
                      </p>
                    </div>
                    <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-3 text-center border border-amber-200 dark:border-amber-800" data-id="element-5805">
                      <p className="text-2xl font-bold text-amber-700 dark:text-amber-400" data-id="element-5806">
                        {autoRunResult.skipped}
                      </p>
                      <p className="text-xs text-amber-600 font-medium mt-0.5" data-id="element-5807">
                        Ya existían
                      </p>
                    </div>
                    <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-3 text-center border border-red-200 dark:border-red-800" data-id="element-5808">
                      <p className="text-2xl font-bold text-red-700 dark:text-red-400" data-id="element-5809">
                        {autoRunResult.errors}
                      </p>
                      <p className="text-xs text-red-600 font-medium mt-0.5" data-id="element-5810">
                        Errores
                      </p>
                    </div>
                  </div>
                </div> : <>
                  <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-5811">
                    Configura el monto mensual por empresa. Al ejecutar, se
                    asignará automáticamente a{' '}
                    <strong data-id="element-5812">todos los empleados</strong> de las empresas activas
                    que aún no tengan balance este mes.
                  </p>

                  <div className="space-y-3" data-id="element-5813">
                    {autoRules.length === 0 ? <div className="text-center py-8 text-gray-500 text-sm" data-id="element-5814">
                        No hay empresas con subsidio activo.
                      </div> : autoRules.map((rule, idx) => {
                const company = getCompany(rule.companyId);
                const empCount = employees.filter(e => String(e.company_id || e.companyId) === rule.companyId).length;
                return <div key={rule.companyId} className={`p-4 rounded-xl border transition-colors ${rule.enabled ? 'border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50'}`} data-id="element-5815">
                            <div className="flex items-center justify-between mb-3" data-id="element-5816">
                              <div data-id="element-5817">
                                <p className="font-bold text-gray-900 dark:text-white text-sm" data-id="element-5818">
                                  {company?.name || rule.companyId}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-5819">
                                  {empCount} empleados
                                </p>
                              </div>
                              <button onClick={() => {
                      const updated = [...autoRules];
                      updated[idx] = {
                        ...rule,
                        enabled: !rule.enabled
                      };
                      saveAutoRules(updated);
                    }} className="flex items-center gap-1.5 text-xs font-bold" data-id="element-5820">
                                {rule.enabled ? <>
                                    <ToggleRight size={22} className="text-blue-600 dark:text-blue-400" data-id="element-5821" />{' '}
                                    <span className="text-blue-600 dark:text-blue-400" data-id="element-5822">
                                      Activa
                                    </span>
                                  </> : <>
                                    <ToggleLeft size={22} className="text-gray-400" data-id="element-5823" />{' '}
                                    <span className="text-gray-400" data-id="element-5824">
                                      Inactiva
                                    </span>
                                  </>}
                              </button>
                            </div>
                            {rule.enabled && <div className="flex items-center gap-2" data-id="element-5825">
                                <span className="text-xs font-bold text-gray-500 shrink-0" data-id="element-5826">
                                  RD$ por empleado:
                                </span>
                                <input type="number" step="0.01" min="0.01" value={rule.amount} onChange={e => {
                      const updated = [...autoRules];
                      updated[idx] = {
                        ...rule,
                        amount: e.target.value
                      };
                      saveAutoRules(updated);
                    }} placeholder="0.00" className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-mono bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-5827" />
                                {rule.amount && !isNaN(parseFloat(rule.amount)) && empCount > 0 && <span className="text-xs text-blue-600 dark:text-blue-400 font-bold shrink-0" data-id="element-5828">
                                      = RD${' '}
                                      {(parseFloat(rule.amount) * empCount).toFixed(0)}{' '}
                                      total
                                    </span>}
                              </div>}
                          </div>;
              })}
                  </div>

                  {autoRunLoading && <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800" data-id="element-5829">
                      <Loader2 size={16} className="animate-spin text-blue-600" data-id="element-5830" />
                      <p className="text-sm text-blue-700 dark:text-blue-300 font-medium" data-id="element-5831">
                        Ejecutando auto-asignación...
                      </p>
                    </div>}
                </>}
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-800 flex gap-3 shrink-0" data-id="element-5832">
              {autoRunResult ? <Button fullWidth onClick={() => setAutoAssignModal(false)} data-id="element-5833">
                  Cerrar
                </Button> : <>
                  <Button variant="outline" onClick={() => setAutoAssignModal(false)} fullWidth disabled={autoRunLoading} data-id="element-5834">
                    Cancelar
                  </Button>
                  <Button onClick={handleRunAutoAssign} disabled={autoRunLoading || autoRules.filter(r => r.enabled && r.amount && parseFloat(r.amount) > 0).length === 0} fullWidth leftIcon={autoRunLoading ? <Loader2 size={16} className="animate-spin" data-id="element-5836" /> : <PlayCircle size={16} data-id="element-5837" />} className="bg-blue-600 hover:bg-blue-700 text-white border-none" data-id="element-5835">
                    {autoRunLoading ? 'Ejecutando...' : `Ejecutar para ${MONTH_NAMES[currentMonth - 1]}`}
                  </Button>
                </>}
            </div>
          </div>
        </div>}

      {/* BULK ASSIGN MODAL */}
      {bulkAssignModal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-id="element-5838">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-lg border border-gray-200 dark:border-gray-800 flex flex-col max-h-[90vh]" data-id="element-5839">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800 shrink-0" data-id="element-5840">
              <div className="flex items-center gap-3" data-id="element-5841">
                <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl" data-id="element-5842">
                  <Zap size={20} className="text-emerald-600 dark:text-emerald-400" data-id="element-5843" />
                </div>
                <div data-id="element-5844">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-5845">
                    Asignación Masiva de Balance
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-5846">
                    Asigna el mismo monto a múltiples empleados
                  </p>
                </div>
              </div>
              <button onClick={() => setBulkAssignModal(false)} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors" data-id="element-5847">
                <X size={18} data-id="element-5848" />
              </button>
            </div>

            <div className="overflow-y-auto flex-1 p-6 space-y-5" data-id="element-5849">
              {bulkDone /* Success Summary */ ? <div className="text-center py-6 space-y-4" data-id="element-5850">
                  <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mx-auto" data-id="element-5851">
                    <CheckCircle2 size={32} className="text-emerald-600 dark:text-emerald-400" data-id="element-5852" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-5853">
                    ¡Asignación completada!
                  </h3>
                  {bulkProgress && <div className="grid grid-cols-3 gap-3 mt-4" data-id="element-5854">
                      <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-3 text-center border border-emerald-200 dark:border-emerald-800" data-id="element-5855">
                        <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-400" data-id="element-5856">
                          {bulkProgress.done}
                        </p>
                        <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium mt-0.5" data-id="element-5857">
                          Asignados
                        </p>
                      </div>
                      <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-3 text-center border border-amber-200 dark:border-amber-800" data-id="element-5858">
                        <p className="text-2xl font-bold text-amber-700 dark:text-amber-400" data-id="element-5859">
                          {bulkProgress.skipped}
                        </p>
                        <p className="text-xs text-amber-600 dark:text-amber-400 font-medium mt-0.5" data-id="element-5860">
                          Ya existían
                        </p>
                      </div>
                      <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-3 text-center border border-red-200 dark:border-red-800" data-id="element-5861">
                        <p className="text-2xl font-bold text-red-700 dark:text-red-400" data-id="element-5862">
                          {bulkProgress.errors}
                        </p>
                        <p className="text-xs text-red-600 dark:text-red-400 font-medium mt-0.5" data-id="element-5863">
                          Errores
                        </p>
                      </div>
                    </div>}
                </div> : <>
                  {/* Company */}
                  <div data-id="element-5864">
                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-5865">
                      Empresa
                    </label>
                    <select value={bulkCompanyId} onChange={e => handleBulkCompanyChange(e.target.value)} disabled={bulkLoading} className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm" data-id="element-5866">
                      <option value="" data-id="element-5867">Seleccionar empresa...</option>
                      {subsidyCompanies.map(c => <option key={c.id} value={String(c.id)} data-id="element-5868">
                          {c.name}
                        </option>)}
                    </select>
                  </div>

                  {/* Month / Year */}
                  <div className="grid grid-cols-2 gap-4" data-id="element-5869">
                    <div data-id="element-5870">
                      <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-5871">
                        Mes
                      </label>
                      <select value={bulkMonth} onChange={e => setBulkMonth(parseInt(e.target.value))} disabled={bulkLoading} className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm" data-id="element-5872">
                        {MONTH_NAMES.map((m, i) => <option key={i} value={i + 1} data-id="element-5873">
                            {m}
                          </option>)}
                      </select>
                    </div>
                    <div data-id="element-5874">
                      <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-5875">
                        Año
                      </label>
                      <input type="number" value={bulkYear} onChange={e => setBulkYear(parseInt(e.target.value))} disabled={bulkLoading} min={2024} max={2099} className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm" data-id="element-5876" />
                    </div>
                  </div>

                  {/* Amount */}
                  <div data-id="element-5877">
                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-5878">
                      Monto por empleado (RD$)
                    </label>
                    <div className="relative" data-id="element-5879">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold text-gray-500" data-id="element-5880">
                        RD$
                      </span>
                      <input type="number" step="0.01" min="0.01" value={bulkAmount} onChange={e => setBulkAmount(e.target.value)} disabled={bulkLoading} placeholder="0.00" className="w-full pl-12 pr-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm font-mono" data-id="element-5881" />
                    </div>
                  </div>

                  {/* Notes */}
                  <div data-id="element-5882">
                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-5883">
                      Notas (opcional)
                    </label>
                    <input type="text" value={bulkNotes} onChange={e => setBulkNotes(e.target.value)} disabled={bulkLoading} placeholder="Ej. Subsidio mensual estándar" className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm" data-id="element-5884" />
                  </div>

                  {/* Employee Selection */}
                  {bulkCompanyId && <div data-id="element-5885">
                      <div className="flex items-center justify-between mb-2" data-id="element-5886">
                        <label className="text-sm font-bold text-gray-700 dark:text-gray-300" data-id="element-5887">
                          Empleados ({bulkSelectedIds.length}/
                          {bulkCompanyEmployees.length} seleccionados)
                        </label>
                        <button onClick={toggleSelectAll} disabled={bulkLoading} className="flex items-center gap-1.5 text-xs font-bold text-emerald-700 dark:text-emerald-400 hover:underline" data-id="element-5888">
                          {bulkSelectedIds.length === bulkCompanyEmployees.length ? <>
                              <CheckSquare size={14} data-id="element-5889" /> Deseleccionar todos
                            </> : <>
                              <Square size={14} data-id="element-5890" /> Seleccionar todos
                            </>}
                        </button>
                      </div>
                      <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden max-h-52 overflow-y-auto" data-id="element-5891">
                        {bulkCompanyEmployees.length === 0 ? <div className="p-4 text-center text-sm text-gray-500" data-id="element-5892">
                            No hay empleados en esta empresa.
                          </div> : bulkCompanyEmployees.map((emp, idx) => {
                  const isSelected = bulkSelectedIds.includes(emp.id);
                  return <button key={emp.id} onClick={() => toggleBulkEmployee(emp.id)} disabled={bulkLoading} className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${idx > 0 ? 'border-t border-gray-100 dark:border-gray-800' : ''} ${isSelected ? 'bg-emerald-50 dark:bg-emerald-900/10' : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'}`} data-id="element-5893">
                                <div className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 transition-colors ${isSelected ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800'}`} data-id="element-5894">
                                  {isSelected && <CheckSquare size={13} data-id="element-5895" />}
                                </div>
                                <div className="h-8 w-8 rounded-full bg-gradient-to-br from-emerald-100 to-emerald-200 dark:from-emerald-900/40 dark:to-emerald-800/40 flex items-center justify-center text-emerald-700 dark:text-emerald-300 font-bold text-xs shrink-0" data-id="element-5896">
                                  {emp.name?.charAt(0) || '?'}
                                </div>
                                <div className="flex-1 min-w-0" data-id="element-5897">
                                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate" data-id="element-5898">
                                    {emp.name}
                                  </p>
                                  <p className="text-xs text-gray-500 truncate" data-id="element-5899">
                                    {emp.email}
                                  </p>
                                </div>
                              </button>;
                })}
                      </div>
                    </div>}

                  {/* Progress bar */}
                  {bulkLoading && bulkProgress && <div className="space-y-2" data-id="element-5900">
                      <div className="flex items-center justify-between text-xs font-medium text-gray-600 dark:text-gray-400" data-id="element-5901">
                        <span className="flex items-center gap-1.5" data-id="element-5902">
                          <Loader2 size={12} className="animate-spin" data-id="element-5903" />
                          Procesando...
                        </span>
                        <span data-id="element-5904">
                          {bulkProgress.done}/{bulkProgress.total}
                        </span>
                      </div>
                      <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-5905">
                        <div className="h-full bg-emerald-500 rounded-full transition-all duration-300" style={{
                  width: `${bulkProgress.done / bulkProgress.total * 100}%`
                }} data-id="element-5906" />
                      </div>
                    </div>}

                  {bulkError && <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl" data-id="element-5907">
                      <AlertCircle size={16} className="text-red-600 dark:text-red-400 shrink-0 mt-0.5" data-id="element-5908" />
                      <p className="text-sm text-red-700 dark:text-red-300" data-id="element-5909">
                        {bulkError}
                      </p>
                    </div>}

                  {/* Summary preview */}
                  {bulkSelectedIds.length > 0 && bulkAmount && !isNaN(parseFloat(bulkAmount)) && <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl border border-emerald-200 dark:border-emerald-800" data-id="element-5910">
                        <p className="text-xs font-bold text-emerald-800 dark:text-emerald-300 uppercase tracking-wide mb-1" data-id="element-5911">
                          Resumen
                        </p>
                        <p className="text-sm text-emerald-700 dark:text-emerald-300" data-id="element-5912">
                          <strong data-id="element-5913">{bulkSelectedIds.length}</strong> empleados ×{' '}
                          <strong className="font-mono" data-id="element-5914">
                            RD$ {parseFloat(bulkAmount).toFixed(2)}
                          </strong>{' '}
                          ={' '}
                          <strong className="font-mono" data-id="element-5915">
                            RD${' '}
                            {(bulkSelectedIds.length * parseFloat(bulkAmount)).toFixed(2)}
                          </strong>{' '}
                          total
                        </p>
                      </div>}
                </>}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 dark:border-gray-800 flex gap-3 shrink-0" data-id="element-5916">
              {bulkDone ? <Button fullWidth onClick={() => setBulkAssignModal(false)} data-id="element-5917">
                  Cerrar
                </Button> : <>
                  <Button variant="outline" onClick={() => setBulkAssignModal(false)} fullWidth disabled={bulkLoading} data-id="element-5918">
                    Cancelar
                  </Button>
                  <Button onClick={handleBulkAssign} disabled={bulkLoading || bulkSelectedIds.length === 0 || !bulkAmount || !bulkCompanyId} fullWidth className="bg-emerald-600 hover:bg-emerald-700 text-white border-none" leftIcon={bulkLoading ? <Loader2 size={16} className="animate-spin" data-id="element-5920" /> : <Zap size={16} data-id="element-5921" />} data-id="element-5919">
                    {bulkLoading ? 'Asignando...' : `Asignar a ${bulkSelectedIds.length} empleados`}
                  </Button>
                </>}
            </div>
          </div>
        </div>}

      {/* ASSIGN BALANCE MODAL (NEW) */}
      {assignModal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-id="element-5922">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-800" data-id="element-5923">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800" data-id="element-5924">
              <div data-id="element-5925">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-5926">
                  Asignar Balance
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-5927">
                  Asigna balance mensual a un empleado
                </p>
              </div>
              <button onClick={() => setAssignModal(false)} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors" data-id="element-5928">
                <X size={18} data-id="element-5929" />
              </button>
            </div>
            <div className="p-6 space-y-5" data-id="element-5930">
              {/* Company selector */}
              <div data-id="element-5931">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-5932">
                  Empresa
                </label>
                <select value={assignForm.companyId} onChange={e => setAssignForm(f => ({
              ...f,
              companyId: e.target.value,
              employeeId: ''
            }))} className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm" data-id="element-5933">
                  <option value="" data-id="element-5934">Seleccionar empresa...</option>
                  {subsidyCompanies.map(c => <option key={c.id} value={String(c.id)} data-id="element-5935">
                      {c.name}
                    </option>)}
                </select>
              </div>
              {/* Employee selector */}
              <div data-id="element-5936">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-5937">
                  Empleado
                </label>
                <select value={assignForm.employeeId} onChange={e => setAssignForm(f => ({
              ...f,
              employeeId: e.target.value
            }))} className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm" disabled={!assignForm.companyId} data-id="element-5938">
                  <option value="" data-id="element-5939">Seleccionar empleado...</option>
                  {employees.filter(e => String(e.company_id || e.companyId) === assignForm.companyId).map(e => <option key={e.id} value={e.id} data-id="element-5940">
                        {e.name}
                      </option>)}
                </select>
              </div>
              {/* Month / Year */}
              <div className="grid grid-cols-2 gap-4" data-id="element-5941">
                <div data-id="element-5942">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-5943">
                    Mes
                  </label>
                  <select value={assignForm.month} onChange={e => setAssignForm(f => ({
                ...f,
                month: parseInt(e.target.value)
              }))} className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm" data-id="element-5944">
                    {MONTH_NAMES.map((m, i) => <option key={i} value={i + 1} data-id="element-5945">
                        {m}
                      </option>)}
                  </select>
                </div>
                <div data-id="element-5946">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-5947">
                    Año
                  </label>
                  <input type="number" value={assignForm.year} onChange={e => setAssignForm(f => ({
                ...f,
                year: parseInt(e.target.value)
              }))} min={2024} max={2099} className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm" data-id="element-5948" />
                </div>
              </div>
              {/* Amount */}
              <div data-id="element-5949">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-5950">
                  Monto (RD$)
                </label>
                <div className="relative" data-id="element-5951">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold text-gray-500" data-id="element-5952">
                    RD$
                  </span>
                  <input type="number" step="0.01" min="0.01" value={assignForm.amount} onChange={e => setAssignForm(f => ({
                ...f,
                amount: e.target.value
              }))} placeholder="0.00" className="w-full pl-12 pr-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm font-mono" data-id="element-5953" />
                </div>
              </div>
              {/* Notes */}
              <div data-id="element-5954">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-5955">
                  Notas (opcional)
                </label>
                <input type="text" value={assignForm.notes} onChange={e => setAssignForm(f => ({
              ...f,
              notes: e.target.value
            }))} placeholder="Ej. Subsidio mensual estándar" className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm" data-id="element-5956" />
              </div>
              {assignError && <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl" data-id="element-5957">
                  <AlertCircle size={16} className="text-red-600 dark:text-red-400 shrink-0 mt-0.5" data-id="element-5958" />
                  <p className="text-sm text-red-700 dark:text-red-300" data-id="element-5959">
                    {assignError}
                  </p>
                </div>}
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-gray-800 flex gap-3" data-id="element-5960">
              <Button variant="outline" onClick={() => setAssignModal(false)} fullWidth data-id="element-5961">
                Cancelar
              </Button>
              <Button onClick={handleAssignBalance} disabled={assignLoading || !assignForm.employeeId || !assignForm.amount} fullWidth data-id="element-5962">
                {assignLoading ? 'Asignando...' : 'Asignar Balance'}
              </Button>
            </div>
          </div>
        </div>}

      {/* Modify Balance Modal */}
      {modifyModal && modifyingBalance && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-id="element-5963">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-800" data-id="element-5964">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800" data-id="element-5965">
              <div data-id="element-5966">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-5967">
                  Ajustar Balance
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-5968">
                  {getEmployee(modifyingBalance.employeeId)?.name} —{' '}
                  {formatMonth(modifyingBalance.month, modifyingBalance.year)}
                </p>
              </div>
              <button onClick={() => setModifyModal(false)} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors" data-id="element-5969">
                <X size={18} data-id="element-5970" />
              </button>
            </div>
            <div className="p-6 space-y-5" data-id="element-5971">
              <div className="grid grid-cols-2 gap-3 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700" data-id="element-5972">
                <div data-id="element-5973">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5" data-id="element-5974">
                    Asignado actual
                  </p>
                  <p className="text-sm font-bold font-mono text-gray-900 dark:text-white" data-id="element-5975">
                    RD$ {modifyingBalance.assignedAmount.toFixed(2)}
                  </p>
                </div>
                <div data-id="element-5976">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5" data-id="element-5977">
                    Disponible actual
                  </p>
                  <p className="text-sm font-bold font-mono text-emerald-600 dark:text-emerald-400" data-id="element-5978">
                    RD$ {modifyingBalance.currentBalance.toFixed(2)}
                  </p>
                </div>
              </div>

              <div data-id="element-5979">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-5980">
                  Monto a Ajustar (RD$)
                </label>
                <div className="relative" data-id="element-5981">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold text-gray-500" data-id="element-5982">
                    RD$
                  </span>
                  <input type="number" step="0.01" value={modifyForm.adjustment} onChange={e => setModifyForm(f => ({
                ...f,
                adjustment: e.target.value
              }))} placeholder="+500 o -200" className="w-full pl-12 pr-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm font-mono" data-id="element-5983" />
                </div>
                <p className="text-xs text-gray-500 mt-1" data-id="element-5984">
                  Ingresa un valor positivo para sumar o negativo para restar.
                </p>
              </div>

              {/* Calculated Preview */}
              {modifyForm.adjustment && !isNaN(parseFloat(modifyForm.adjustment)) && <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-800" data-id="element-5985">
                    <p className="text-xs font-bold text-blue-800 dark:text-blue-300 mb-2 uppercase tracking-wide" data-id="element-5986">
                      Resultado Previsto
                    </p>
                    <div className="flex justify-between text-sm" data-id="element-5987">
                      <span data-id="element-5988">Nuevo Asignado:</span>
                      <span className="font-mono font-bold text-gray-900 dark:text-white" data-id="element-5989">
                        RD${' '}
                        {(modifyingBalance.assignedAmount + parseFloat(modifyForm.adjustment)).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm mt-1" data-id="element-5990">
                      <span data-id="element-5991">Nuevo Disponible:</span>
                      <span className="font-mono font-bold text-gray-900 dark:text-white" data-id="element-5992">
                        RD${' '}
                        {(modifyingBalance.currentBalance + parseFloat(modifyForm.adjustment)).toFixed(2)}
                      </span>
                    </div>
                  </div>}

              <div data-id="element-5993">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-5994">
                  Motivo del ajuste (opcional)
                </label>
                <input type="text" value={modifyForm.notes} onChange={e => setModifyForm(f => ({
              ...f,
              notes: e.target.value
            }))} placeholder="Ej. Bono adicional, corrección..." className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm" data-id="element-5995" />
              </div>

              {modifyError && <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl" data-id="element-5996">
                  <AlertCircle size={16} className="text-red-600 dark:text-red-400 shrink-0 mt-0.5" data-id="element-5997" />
                  <p className="text-sm text-red-700 dark:text-red-300" data-id="element-5998">
                    {modifyError}
                  </p>
                </div>}
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-gray-800 flex gap-3" data-id="element-5999">
              <Button variant="outline" onClick={() => setModifyModal(false)} fullWidth data-id="element-6000">
                Cancelar
              </Button>
              <Button onClick={handleModifyBalance} disabled={modifyLoading || !modifyForm.adjustment} fullWidth data-id="element-6001">
                {modifyLoading ? 'Guardando...' : 'Aplicar Ajuste'}
              </Button>
            </div>
          </div>
        </div>}
    </div>;
}